import { useCallback, useEffect, useRef, useState } from 'react';

const WS_URL =
  import.meta.env.VITE_WS_URL ||
  `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;

/**
 * Chat WebSocket hook. Manages the connection lifecycle and the streaming
 * message protocol. Consumers pass callbacks for each server event.
 */
export function useChatSocket({ onUserMessage, onToken, onDone, onError, onConversation } = {}) {
  const [status, setStatus] = useState('connecting'); // connecting | open | closed
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const handlers = useRef({});
  handlers.current = { onUserMessage, onToken, onDone, onError, onConversation };

  const connect = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState <= 1) return;
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    setStatus('connecting');

    ws.onopen = () => setStatus('open');
    ws.onclose = () => {
      setStatus('closed');
      // Auto-reconnect with backoff.
      clearTimeout(reconnectRef.current);
      reconnectRef.current = setTimeout(connect, 2500);
    };
    ws.onerror = () => ws.close();
    ws.onmessage = (event) => {
      let msg;
      try {
        msg = JSON.parse(event.data);
      } catch {
        return;
      }
      const h = handlers.current;
      switch (msg.type) {
        case 'user_message':
          h.onUserMessage?.(msg.message, msg.conversationId);
          break;
        case 'conversation':
          h.onConversation?.(msg.conversation);
          break;
        case 'token':
          h.onToken?.(msg.delta, msg.conversationId);
          break;
        case 'done':
          h.onDone?.(msg.message, msg.conversationId);
          break;
        case 'error':
          h.onError?.(msg);
          break;
        default:
          break;
      }
    };
  }, []);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectRef.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const sendChat = useCallback(({ conversationId, content, model }) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify({ type: 'chat', conversationId, content, model }));
    return true;
  }, []);

  return { status, sendChat };
}
